import React, { useEffect, useState } from 'react';
import { Crown, KeyRound, Users, DollarSign, ShoppingCart, TrendingUp, XCircle } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { ADMIN_NAV } from '../../constants/nav';
import { useAuth } from '../../features/auth/AuthContext';
import { StatCard } from '../../components/common/StatCard';
import { DASHBOARD } from '../../constants/testIds';
import { fetchAdminStats, AdminStats } from '../../services/api/admin';
import { adminSalesAnalytics, adminSalesTrend, SalesAnalytics, SalesTrendPoint } from '../../features/orders/api/orders';
import { formatPrice } from '../../features/courses/api/courses';
import { api } from '../../services/api/client';

interface RejectionBucket { reason: string; count: number }
interface RejectionAnalytics { total: number; buckets: RejectionBucket[] }

export default function AdminDashboard() {
  const { user } = useAuth();
  const [sales, setSales] = useState<SalesAnalytics | null>(null);
  const [trend, setTrend] = useState<SalesTrendPoint[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [period, setPeriod] = useState<'daily' | 'monthly'>('daily');
  const [rejections, setRejections] = useState<RejectionAnalytics | null>(null);

  useEffect(() => { adminSalesAnalytics().then(setSales).catch(() => {}); }, []);
  useEffect(() => { fetchAdminStats().then(setStats).catch(() => {}); }, []);
  useEffect(() => { adminSalesTrend(period).then((r) => setTrend(r.points)).catch(() => {}); }, [period]);
  useEffect(() => {
    api.get<RejectionAnalytics>('/admin/vip/analytics/rejection-reasons')
      .then((r) => setRejections(r.data))
      .catch(() => setRejections({ total: 0, buckets: [] }));
  }, []);

  const pBtn = (p: 'daily' | 'monthly', label: string) => (
    <button data-testid={`trend-period-${p}`} onClick={() => setPeriod(p)} className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${period === p ? 'bg-cyan-400 text-[#050914] font-medium' : 'text-white/60 hover:text-white hover:bg-white/5'}`}>{label}</button>
  );

  return (
    <DashboardLayout navItems={ADMIN_NAV} title="Admin Dashboard">
      <div data-testid={DASHBOARD.adminRoot} className="max-w-6xl">
        <div data-testid={DASHBOARD.welcomeCard} className="bg-gradient-to-br from-cyan-400/10 to-transparent border border-cyan-400/20 rounded-2xl p-8 mb-6">
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-400 mb-2">Admin Panel</p>
          <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Halo, {user?.full_name}</h2>
          <p className="text-white/60 mt-2 text-sm">Kelola komunitas Cuantrader dari satu tempat.</p>
        </div>

        <div className="flex items-center gap-2 mb-3 text-white/70">
          <TrendingUp className="w-4 h-4 text-cyan-400" strokeWidth={1.5} />
          <h3 className="text-sm uppercase tracking-wider">Sales Analytics</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6" data-testid="admin-sales-analytics">
          <StatCard icon={DollarSign} label="Total Pendapatan" testId="sales-revenue-card" accent>
            <p className="text-3xl font-light text-white">{sales ? formatPrice(sales.revenue, sales.currency) : '—'}</p>
            <p className="text-sm text-white/50 mt-1">Dari {sales?.paid ?? 0} pesanan lunas.</p>
          </StatCard>
          <StatCard icon={ShoppingCart} label="Pesanan" testId="sales-orders-card">
            <p className="text-3xl font-light text-white">{sales?.total_orders ?? '—'}</p>
            <p className="text-sm text-white/50 mt-1">{sales?.pending ?? 0} pending · {sales?.failed ?? 0} gagal · {sales?.expired ?? 0} kedaluwarsa</p>
          </StatCard>
          <StatCard icon={TrendingUp} label="Konversi Pembayaran" testId="sales-conversion-card">
            <p className="text-3xl font-light text-white">{sales ? `${sales.conversion_rate}%` : '—'}</p>
            <p className="text-sm text-white/50 mt-1">Pesanan lunas dibanding total.</p>
          </StatCard>
        </div>

        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 mb-8" data-testid="sales-trend-chart">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-white/70">Tren Pendapatan</p>
            <div className="flex gap-1 bg-white/[0.03] border border-white/10 rounded-lg p-1">{pBtn('daily', 'Harian')}{pBtn('monthly', 'Bulanan')}</div>
          </div>
          {trend.length === 0 ? (
            <p className="text-white/30 text-sm text-center py-16">Belum ada data pendapatan.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={trend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#22d3ee" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} tickFormatter={(v) => (period === 'daily' ? v.slice(5) : v)} />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} width={40} />
                <Tooltip contentStyle={{ background: '#090D1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff' }} formatter={(v: any) => [formatPrice(v, 'IDR'), 'Pendapatan']} />
                <Area type="monotone" dataKey="revenue" stroke="#22d3ee" strokeWidth={2} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="flex items-center gap-2 mb-3 text-white/70">
          <Users className="w-4 h-4 text-cyan-400" strokeWidth={1.5} />
          <h3 className="text-sm uppercase tracking-wider">Pertumbuhan User</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          <StatCard icon={Users} label="User Baru Hari Ini" testId="growth-new-today" accent>
            <p className="text-3xl font-light text-white">{stats?.new_today ?? '—'}</p>
            <p className="text-sm text-white/50 mt-1">Registrasi hari ini.</p>
          </StatCard>
          <StatCard icon={Users} label="User Baru Bulan Ini" testId="growth-new-month">
            <p className="text-3xl font-light text-white">{stats?.new_this_month ?? '—'}</p>
            <p className="text-sm text-white/50 mt-1">Registrasi bulan berjalan.</p>
          </StatCard>
          <StatCard icon={Users} label="Total Members" testId="growth-total">
            <p className="text-3xl font-light text-white">{stats?.total_members ?? '—'}</p>
            <p className="text-sm text-white/50 mt-1">Seluruh member terdaftar.</p>
          </StatCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2 bg-white/[0.02] border border-white/10 rounded-2xl p-5" data-testid="growth-chart">
            <p className="text-sm text-white/70 mb-4">User Baru — 14 Hari Terakhir</p>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={stats?.daily || []} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="grw" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#34d399" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#34d399" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis allowDecimals={false} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} width={30} />
                <Tooltip contentStyle={{ background: '#090D1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff' }} formatter={(v: any) => [v, 'User baru']} />
                <Area type="monotone" dataKey="count" stroke="#34d399" strokeWidth={2} fill="url(#grw)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5" data-testid="referral-breakdown">
            <p className="text-sm text-white/70 mb-4">Sumber Referral</p>
            {(stats?.referral || []).length === 0 ? (
              <p className="text-white/30 text-sm">Belum ada data.</p>
            ) : (
              <div className="space-y-2">
                {(stats?.referral || []).slice(0, 8).map((r) => (
                  <div key={r.source} className="flex items-center justify-between text-sm border-b border-white/5 pb-2 last:border-0">
                    <span className="text-white/70 capitalize truncate mr-2">{r.source}</span>
                    <span className="text-cyan-400 font-medium shrink-0">{r.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 mb-6" data-testid="rejection-analytics-card">
          <div className="flex items-center gap-2 mb-3 text-white/70">
            <XCircle className="w-4 h-4 text-red-400" strokeWidth={1.5} />
            <h3 className="text-sm uppercase tracking-wider">Alasan Penolakan VIP</h3>
            {rejections && rejections.total > 0 && (
              <span className="ml-2 text-xs text-white/40">Total: {rejections.total}</span>
            )}
          </div>
          {rejections === null ? (
            <p className="text-white/40 text-sm py-6">Memuat...</p>
          ) : rejections.buckets.length === 0 ? (
            <p className="text-white/40 text-sm py-6">Belum ada penolakan VIP.</p>
          ) : (
            <div className="space-y-2">
              {rejections.buckets.map((b) => {
                const pct = rejections.total ? Math.round((b.count / rejections.total) * 100) : 0;
                return (
                  <div key={b.reason} className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1 gap-3">
                        <p className="text-sm text-white/90 truncate" title={b.reason}>{b.reason}</p>
                        <p className="text-xs text-white/50 flex-shrink-0">{b.count} · {pct}%</p>
                      </div>
                      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full bg-red-400/70" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <StatCard icon={Crown} label="Pending VIP" testId={DASHBOARD.pendingVipCard}>
            <p className="text-3xl font-light text-white">{stats ? stats.pending_vip : '—'}</p>
            <p className="text-sm text-white/50 mt-1">Permintaan VIP menunggu persetujuan.</p>
          </StatCard>
          <StatCard icon={KeyRound} label="Request Indikator" testId={DASHBOARD.requestIndikatorCard}>
            <p className="text-3xl font-light text-white">{stats ? stats.pending_indicator : '—'}</p>
            <p className="text-sm text-white/50 mt-1">Permintaan akses indikator baru.</p>
          </StatCard>
          <StatCard icon={Users} label="Total Members" testId={DASHBOARD.usersCard}>
            <p className="text-3xl font-light text-white">{stats ? stats.total_members : '—'}</p>
            <p className="text-sm text-white/50 mt-1">Lihat detail di menu Users.</p>
          </StatCard>
        </div>
      </div>
    </DashboardLayout>
  );
}
